import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, MapPin, Calendar, Euro, ArrowRight, TrendingDown } from 'lucide-react';
import { budgetReizen } from '../budgetData';

const gradients = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
];

const BudgetCard = ({ reis, index }) => {
  const [showMore, setShowMore] = useState(false);
  
  const imageStyle = reis.image_url
    ? { backgroundImage: `url(${reis.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: gradients[index % gradients.length] };
  
  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
      {/* Image */}
      <div 
        className="h-56 relative"
        style={imageStyle}
      >
        <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg flex items-center gap-2">
          <TrendingDown className="w-4 h-4" />
          Budget Deal
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-2xl font-bold text-white mb-1">{reis.naam}</h3>
          <div className="flex items-center gap-4 text-white/90 text-sm">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{reis.dagen} dagen</span>
            </div>
            <div className="flex items-center gap-1">
              <Euro className="w-4 h-4" />
              <span className="font-bold">Vanaf €{reis.prijs}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h4 className="text-sm font-semibold text-green-700 mb-3">✨ Hoogtepunten:</h4>
        <div className="space-y-2 mb-4">
          {reis.hoogtepunten.map((punt) => (
            <div key={punt} className="flex items-start gap-2">
              <span className="text-sm text-gray-700">{punt}</span>
            </div>
          ))}
        </div>
        
        <div className="mb-4">
          <p className={`text-sm text-gray-600 ${!showMore && 'line-clamp-2'}`}>
            {reis.beschrijving}
          </p>
          <button
            onClick={() => setShowMore(!showMore)}
            className="text-green-700 text-sm font-medium mt-1 hover:text-green-900 transition-colors"
          >
            {showMore ? '↑ Minder' : '↓ En nog meer'}
          </button>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-green-800 font-semibold">
            💰 Bespaar tot wel €1000 vergeleken met reguliere reizen!
          </p>
        </div>

        <Link
          to="/contact"
          className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg font-semibold flex items-center justify-center gap-2 hover:from-green-500 hover:to-green-400 transition-all"
        >
          Vraag Offerte Aan
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </div>
  );
};

const BudgetReizen = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-blue-50">
      {/* Hero Section */}
      <section 
        className="relative py-20 px-6 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80)',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 via-green-800/90 to-blue-900/90" />
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-block p-4 bg-white/10 backdrop-blur-sm rounded-full mb-6">
            <TrendingDown className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Budget Reizen
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
            Ontdek prachtige bestemmingen zonder je portemonnee leeg te maken! 
            Onze budget reizen bieden geweldige ervaringen tegen betaalbare prijzen.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-white">
            <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
              <p className="text-sm font-medium">✅ Betaalbare Prijzen</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
              <p className="text-sm font-medium">✅ Topbestemmingen</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
              <p className="text-sm font-medium">✅ Complete Ervaringen</p>
            </div>
          </div>
        </div>
      </section>

      {/* Europa Section */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <TrendingDown className="w-8 h-8 text-green-700" />
            <h2 className="text-4xl font-bold text-green-700">Europa Budget Reizen</h2>
          </div>
          <p className="text-lg text-gray-600 mb-8">
            Van historische steden tot prachtige stranden - Europa voor een vriendelijke prijs!
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {budgetReizen.europa.map((reis, index) => (
              <BudgetCard key={reis.id} reis={reis} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Azië Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-blue-50 to-green-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <TrendingDown className="w-8 h-8 text-blue-700" />
            <h2 className="text-4xl font-bold text-blue-700">Azië Budget Reizen</h2>
          </div>
          <p className="text-lg text-gray-600 mb-8">
            Ontdek het betoverende Azië zonder je budget te overschrijden
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {budgetReizen.azie.map((reis, index) => (
              <BudgetCard key={reis.id} reis={reis} index={index + budgetReizen.europa.length} />
            ))}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-2xl p-8 shadow-lg">
            <h3 className="text-3xl font-bold text-green-900 mb-6 text-center">
              Waarom Kiezen voor Budget Reizen?
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-xl font-bold text-green-700 mb-3">💰 Betaalbaar Reizen</h4>
                <p className="text-gray-700">
                  Geniet van prachtige bestemmingen zonder je zorgen te maken over hoge kosten. 
                  Onze budget reizen zijn perfect voor studenten, jonge reizigers en iedereen die slim wil reizen.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-xl font-bold text-blue-700 mb-3">🎒 Echte Ervaringen</h4>
                <p className="text-gray-700">
                  Budget reizen betekent niet minder plezier! Ontdek lokale culturen, 
                  ontmoet andere reizigers en creëer onvergetelijke herinneringen.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-xl font-bold text-purple-700 mb-3">🗺️ Complete Rondreizen</h4>
                <p className="text-gray-700">
                  Elk budget pakket bevat de belangrijkste hoogtepunten van de bestemming. 
                  Je mist niets, maar betaalt minder!
                </p>
              </div>
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-xl font-bold text-orange-700 mb-3">✨ Flexibel & Persoonlijk</h4>
                <p className="text-gray-700">
                  Wil je iets aanpassen? Geen probleem! We passen elk budget pakket 
                  aan naar jouw wensen en budget.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-green-700 to-blue-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Klaar Voor Jouw Budget Avontuur?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Neem contact op voor een vrijblijvende offerte of kijk bij onze maatwerkreizen voor meer opties.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="px-8 py-4 bg-white text-green-700 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all shadow-lg"
            >
              Vraag Offerte Aan
            </Link>
            <Link
              to="/maatwerkreizen"
              className="px-8 py-4 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-500 transition-all shadow-lg"
            >
              Bekijk Alle Maatwerkreizen
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BudgetReizen;
